import React, { useEffect, useRef, useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import moment from "moment";
import { setStepIndex } from "../../../redux/globalJoyrideSlice";
import { useDispatch } from "react-redux";

export default function FlatEventListSelector({
  itemType,
  categories = {},
  title = "Select Items",
  onSelect,
  onBulkSubmit,
  alreadySubmittedIds = [],
  userData,
  onAlreadySubmitted,
}) {
  const [openCategories, setOpenCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editRows, setEditRows] = useState({});
  const [isValidEdit, setIsValidEdit] = useState(true);

  const dispatch = useDispatch();
  const hasMovedToStep2 = useRef(false);
  const submitRef = useRef(null);


  useEffect(() => {
    let allValid = true;
    for (const id in editRows) {
      const rowData = editRows[id];
      if (rowData.start_date && rowData.end_date) {
        const startDate = moment(rowData.start_date);
        const endDate = moment(rowData.end_date);
        if (startDate.isAfter(endDate)) {
          allValid = false;
          break;
        }
      }
    }
    setIsValidEdit(allValid);
  }, [editRows]);

  const colorClasses = ["#007BFF", "#FFC107"]; // Custom blue, yellow
  const dateColorMap = {};
  const colorToggleState = {};

  const toggleCheckbox = (item) => {
    if (alreadySubmittedIds.includes(item.id)) {
      alert("This awareness day is already submitted.");
      return;
    }
    setSelectedItems((prev) => {
      const exists = prev.find((h) => h.id === item.id);

      const next = exists ? prev.filter((h) => h.id !== item.id) : [...prev, item];

      // When a new item is selected, scroll to submit and show the confirmation popup (stepIndex 3)
      if (!exists) {
        setTimeout(() => {
          submitRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          dispatch(setStepIndex(3));
        }, 300);
      }

      return next;
    });
  };


  const scrollToUncheckedItem = () => {
    const container = document.querySelector(".joyride-inner-scroll");
    const target = document.querySelector(
      '[data-tour="celebration-checkbox-item"]'
    );

    if (container && target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };


  const toggleCategory = (categoryName) => {
    setOpenCategories((prev) => {
      const isOpening = !prev.includes(categoryName);

      if (isOpening && !hasMovedToStep2.current) {
        // 1️⃣ Wait for Collapse animation
        setTimeout(() => {
          // 2️⃣ Scroll FIRST
          scrollToUncheckedItem();

          // 3️⃣ THEN start Joyride step
          setTimeout(() => {
            dispatch(setStepIndex(2));
            hasMovedToStep2.current = true;
          }, 300);
        }, 400);
      }

      return isOpening
        ? [...prev, categoryName]
        : prev.filter((cat) => cat !== categoryName);
    });
  };

  const updateField = (id, field, value) => {
    setEditRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  return (
    <>
      <div className="  border rounded p-3">
        <h5 className="mb-3">{title}</h5>

        <div className="item-categories">
          {Object.entries(categories).map(([categoryName, items]) => (
            <div key={categoryName} className="mb-3">
              <Button
                variant="outline-primary"
                onClick={() => toggleCategory(categoryName)}
                aria-controls={`collapse-${categoryName}`}
                aria-expanded={openCategories.includes(categoryName)}
                className="w-100 text-start"
              >
                {categoryName.toUpperCase()} ({items?.length || 0})
              </Button>

                <Collapse in={openCategories.includes(categoryName)}>
                  <div>
                    {Array.isArray(items) && items.length > 0 ? (
                      <div id={`collapse-${categoryName}`} className="mt-2">
                        <div className="list-group">
                          {[...items]
                            .sort((a, b) => {
                              const dateA = moment(a.start_date || "9999-12-31");
                              const dateB = moment(b.start_date || "9999-12-31");
                              return dateA.diff(dateB);
                            })
                            .map((item) => {
                              const isSelected = selectedItems.some(
                                (h) => h.id === item.id
                              );
                              const isEditing = !!editRows[item.id];
                              const rowData = isEditing ? editRows[item.id] : item;

                              const validStart =
                                item.start_date && item.start_date !== "null";
                              const validEnd =
                                item.end_date && item.end_date !== "null";

                              const dateKey = validStart
                                ? moment(item.start_date).format("YYYY-MM-DD")
                                : "NO_DATE";

                              if (!dateColorMap[categoryName]) {
                                dateColorMap[categoryName] = {};
                                colorToggleState[categoryName] = 0;
                              }

                              if (!dateColorMap[categoryName][dateKey]) {
                                const colorIndex =
                                  colorToggleState[categoryName] % 2;
                                dateColorMap[categoryName][dateKey] =
                                  colorClasses[colorIndex];
                                colorToggleState[categoryName]++;
                              }

                              const badgeColor =
                                dateColorMap[categoryName][dateKey];

                              return (
                                <div
                                  key={item.id}
                                  className="list-group-item"
                                  {...(!isSelected && !alreadySubmittedIds.includes(item.id)
                                    ? { "data-tour": "celebration-checkbox-item" }
                                    : {})}
                                >
                                  <div className="d-flex align-items-center mb-2">
                                    {userData?.roles === "user" ? (
                                      <span
                                        className="me-2"
                                        style={{
                                          fontSize: "20px",
                                          lineHeight: "20px",
                                        }}
                                      >
                                        •
                                      </span>
                                    ) : (
                                      <input
                                        type="checkbox"
                                        className="form-check-input me-2"
                                        checked={
                                          isSelected ||
                                          alreadySubmittedIds.includes(item.id)
                                        }
                                        onChange={() => {
                                          if (
                                            alreadySubmittedIds.includes(item.id)
                                          ) {
                                            alert("This day is already submitted.");
                                            onAlreadySubmitted?.();
                                            return;
                                          }
                                          toggleCheckbox(item);
                                        }}
                                      />
                                    )}
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center">
                                        <span
                                          className="rounded-pill me-2 px-2 py-1 text-white"
                                          style={{
                                            fontSize: "0.9em",
                                            backgroundColor: badgeColor,
                                          }}
                                        >
                                          {validStart
                                            ? moment(item.start_date).format(
                                              "MMM DD (ddd)"
                                            )
                                            : "add date"}
                                          {validEnd &&
                                            item.start_date !== item.end_date &&
                                            ` - ${moment(item.end_date).format(
                                              "MMM DD (ddd)"
                                            )}`}
                                        </span>
                                        <strong>{item.name || item.details}</strong>
                                      </div>
                                    </div>
                                    {userData?.roles !== "user" && (
                                      <button
                                        className="btn btn-sm btn-warning ms-2"
                                        onClick={() =>
                                          setEditRows((prev) =>
                                            prev[item.id]
                                              ? { ...prev, [item.id]: undefined }
                                              : { ...prev, [item.id]: item }
                                          )
                                        }
                                      >
                                        {isEditing ? "Cancel" : "Edit"}
                                      </button>
                                    )}
                                  </div>

                                  {isEditing && (
                                    <div className="row gx-2 gy-1">
                                      <div className="col-md-3">
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={
                                            rowData.name || rowData.details || ""
                                          }
                                          onChange={(e) =>
                                            updateField(
                                              item.id,
                                              "name",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Name"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <input
                                          type="date"
                                          className="form-control"
                                          value={
                                            rowData.start_date
                                              ? moment(rowData.start_date).format(
                                                "YYYY-MM-DD"
                                              )
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateField(
                                              item.id,
                                              "start_date",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <input
                                          type="date"
                                          className="form-control"
                                          value={
                                            rowData.end_date
                                              ? moment(rowData.end_date).format(
                                                "YYYY-MM-DD"
                                              )
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateField(
                                              item.id,
                                              "end_date",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted px-3 mt-2">
                        No items in this category.
                      </div>
                    )}
                  </div>
                </Collapse>          
            </div>
          ))}
        </div>

        {selectedItems.length > 0 && (
          <div
            ref={submitRef}
            className="mt-4"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            <button
              className="btn btn-success"
              data-tour="celebration-submit-button"
              onClick={() => {
                if (!isValidEdit) {
                  alert(
                    "Error: The start date cannot be after the end date. Please correct the dates before submitting."
                  );
                  return;
                }
                const dataToSubmit = selectedItems.map((item) =>
                  editRows[item.id] ? { ...item, ...editRows[item.id] } : item
                );
                onBulkSubmit(dataToSubmit);
              }}
            >
              Submit Selected ({selectedItems.length})
            </button>
          </div>
        )}
      </div>
    </>
  );
}
